import OrderRow from './orderrow';
import Location from './location';
import ActionItem from './actionitem';
import Customer from './customer';
import Address from './address';
import ElectronicInvoiceInfo from './electronicinvoiceinfo';
import { sumBy } from 'lodash';
import { orderStatusToText } from './orderstatus';

class Order {
    constructor(json) {
        if (json != null) {
            this.id = json.id;
            this.storageId = json.storageId;
            this.status = json.status;
            if (json.location) this.location = new Location(json.location);
            if (json.customer != null) {
                this.customer = new Customer(json.customer);
            }
            this.orderedFilters = [];
            if (json.orderedFilters != null && json.orderedFilters.length > 0) {
                for (let f of json.orderedFilters) {
                    this.orderedFilters.push(new OrderRow(f));
                }
            }
            this.orderTotalPriceWithoutVat = json.orderTotalPriceWithoutVat;
            this.vatPercent = json.vatPercent;
            this.vatAmount = json.vatAmount;
            this.orderTotalPrice = json.orderTotalPrice;
            this.deliveryTime = json.deliveryTime;
            this.deliveryTerms = json.deliveryTerms;
            this.ordererName = json.ordererName;
            this.ordererTel = json.ordererTel;
            this.contractNumberId = json.contractNumberId;
            this.contractNumber = json.contractNumber;
            this.deliveryRecipient = json.deliveryRecipient;
            this.deliveryAddress = new Address(json.deliveryAddress);
            this.deliveryCustomerNumber = json.deliveryCustomerNumber;
            this.billingResponsible = json.billingResponsible;
            if (json.billingAddress) this.billingAddress = new Address(json.billingAddress);
            if (json.electronicInvoiceAddress)
                this.electronicInvoiceAddress = new ElectronicInvoiceInfo(json.electronicInvoiceAddress);
            this.billingResponsibleBusinessId = json.billingResponsibleBusinessId;
            this.billingResponsibleCustomerNumber = json.billingResponsibleCustomerNumber;
            this.orderSent = json.orderSent;
            this.orderForm = json.orderForm;
            this.reference = json.reference;
            this.confirmationEmail = json.confirmationEmail;
            if (json.action) this.action = new ActionItem(json.action);

            this.orderSender = json.orderSender;
            this.portfolioId = json.portfolioId;
            this.maintenanceArea = json.maintenanceArea;
            this.packingInstructions = json.packingInstructions;
            this.deliveryMethod = json.deliveryMethod;
            this.billingTerms = json.billingTerms;
            this.invoiceNumbers = json.invoiceNumbers;
            this.deliveryInfo = json.deliveryInfo;
            this.orderNumber = json.orderNumber;
            this.publicReference = json.publicReference;
            this.orderType = json.orderType;
            this.billingResponsibleVatId = json.billingResponsibleVatId;
            this.ourReference = json.ourReference;
            this.generalInfo = json.generalInfo;
            this.combinedOrderInfoBase64 = json.combinedOrderInfoBase64;
            this.usesCapacityAllocation = json.usesCapacityAllocation;
            this.earlyDeliveryTime = json.earlyDeliveryTime;
            this.emailInvoiceAddress = json.emailInvoiceAddress;
            this.orderToPrivateCustomer = json.orderToPrivateCustomer;
            this.deliveryAddressMustBeSelectedInCart = json.deliveryAddressMustBeSelectedInCart;
            this.deliveryTermsImmutable = json.deliveryTermsImmutable;
            this.invoicingInfoBase64 = json.invoicingInfoBase64;
            this.deliveryConfirmationEmail = json.deliveryConfirmationEmail;
            this.orderToWarehouseId = json.orderToWarehouseId;

            // Sweden expansion -- see doc/sweden-expansion-currency-plan.md. Must be preserved on
            // round-trip (edit + save an existing order): InsertOrUpdate() re-inserts a new order row
            // straight from this object with no re-resolution of tenant/currency, so dropping these here
            // would silently corrupt/break the save.
            this.tenantCompanyId = json.tenantCompanyId;
            this.currency = json.currency;
            this.exchangeRateUsed = json.exchangeRateUsed;
        } else {
            this.id = 0;
            this.storageId = 0;
            this.status = 0;
            this.location = null;
            this.customer = null;
            this.orderedFilters = [];
            this.orderTotalPriceWithoutVat = 0;
            this.vatPercent = 0;
            this.vatAmount = 0;
            this.orderTotalPrice = 0;
            this.deliveryTime = '';
            this.deliveryTerms = '';
            this.ordererName = '';
            this.ordererTel = '';
            this.contractNumberId = 0;
            this.contractNumber = '';
            this.deliveryRecipient = '';
            this.deliveryAddress = null;
            this.deliveryCustomerNumber = null;
            this.billingResponsible = '';
            this.billingAddress = null;
            this.electronicInvoiceAddress = null;
            this.billingResponsibleBusinessId = '';
            this.billingResponsibleCustomerNumber = null;
            this.orderSent = null;
            this.orderForm = [];
            this.reference = '';
            this.confirmationEmail = '';
            this.maintenanceArea = '';
            this.packingInstructions = '';
            this.orderNumber = 0;
            this.publicReference = null;
            this.deliveryAddressMustBeSelectedInCart = false;
            this.deliveryTermsImmutable = false;
            this.deliveryConfirmationEmail = '';
            this.tenantCompanyId = null;
            this.currency = null;
            this.exchangeRateUsed = null;
        }
    }

    static StatusOrdered() {
        return 4;
    }
    static StatusPacked() {
        return 6;
    }
    static StatusLoaded() {
        return 7;
    }
    static StatusDelivered() {
        return 8;
    }
    static StatusDeliveredNonInvoicable() {
        return 9;
    }

    orderStatusText() {
        return orderStatusToText(this.status);
    }
    // Display-only helper: prefer the persisted, prefixed public_reference (e.g. "SE12345") over the
    // bare orderNumber. Never use this for sorting/lookup -- it's not numeric/comparable across tenants.
    // See doc/sweden-expansion-order-number-prefix-plan.md.
    getDisplayOrderNumber() {
        return this.publicReference || this.orderNumber;
    }
    getDeliveryInfo() {
        return this.deliveryAddress != null
            ? `${this.deliveryRecipient}, ${this.deliveryAddress.streetAddress}, ${this.deliveryAddress.zipCode} ${this.deliveryAddress.city}`
            : null;
    }
    getBillingAddressInfo() {
        return this.billingAddress != null
            ? `${this.billingResponsible}, ${this.billingAddress.streetAddress}, ${this.billingAddress.zipCode} ${this.billingAddress.city}`
            : null;
    }
    getElectronicInvoiceAddressInfo() {
        return this.electronicInvoiceAddress != null
            ? `${this.electronicInvoiceAddress.ovt}, ${this.electronicInvoiceAddress.operatorBrokerId}, ${this.electronicInvoiceAddress.operator}`
            : null;
    }

    updateTotalPrice() {
        for (let r of this.orderedFilters) {
            r.updateTotalPrice();
        }
        this.orderTotalPriceWithoutVat = sumBy(this.orderedFilters, 'totalPriceWithoutVat');
        this.vatAmount = (this.vatPercent / 100) * this.orderTotalPriceWithoutVat;
        this.orderTotalPrice = this.orderTotalPriceWithoutVat + this.vatAmount;
    }

    static OrderTypeEarly = () => 'Early';
    static OrderTypeNormal = () => 'Normal';
    static OrderTypeUrgent = () => 'Urgent';
}

export default Order;

// Display-only helper usable for any order-shaped object (Order/ProductionOrder class instances or plain
// JSON), so every UI surface shows the same string consistently. Never use for sorting/lookup/routing --
// not numeric or comparable across tenants. See doc/sweden-expansion-order-number-prefix-plan.md.
export function getOrderDisplayNumber(order) {
    if (!order) return order;
    return order.publicReference || order.orderNumber;
}
